import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  CreateCompanyRequestBillingCycle,
  CreateCompanyRequestPlanKey,
} from '@core/api/workspace/models';
import type { SignupAuthDataResponse } from '@core/api/workspace/models/signupAuthDataResponse';
import type { SignupWorkspaceResponse } from '@core/api/workspace/models/signupWorkspaceResponse';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import { TokenStorageService } from '@core/services/token-storage.service';

export interface NavigateAfterLoginOptions {
  workspace?: SignupWorkspaceResponse;
  returnUrl?: string;
}

export interface NavigateAfterUserAuthOptions {
  workspace?: SignupWorkspaceResponse;
  workspaceAccessCount?: number;
  workspaceIds?: string[];
  returnUrl?: string;
  planKey?: string;
  billingCycle?: string;
}

export function navigateToCreateCompany(
  router: Router,
  queryParams?: { planKey?: string; billingCycle?: string; companyName?: string },
): Promise<boolean> {
  return router.navigate(['/create-company'], {
    queryParams: {
      planKey: queryParams?.planKey ?? CreateCompanyRequestPlanKey.free,
      billingCycle: queryParams?.billingCycle ?? CreateCompanyRequestBillingCycle.MONTHLY,
      ...(queryParams?.companyName ? { companyName: queryParams.companyName } : {}),
    },
  });
}

export function navigateAfterLogin(
  router: Router,
  options: NavigateAfterLoginOptions,
): Promise<boolean> {
  if (options.workspace) {
    const returnUrl = options.returnUrl;
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      return router.navigateByUrl(returnUrl);
    }

    return router.navigate(['/dashboard']);
  }

  return navigateToCreateCompany(router);
}

export async function switchToWorkspace(
  userAPIService: WorkspaceAPIService,
  tokenStorage: TokenStorageService,
  workspaceId: string,
): Promise<SignupAuthDataResponse> {
  const response = await firstValueFrom(
    userAPIService.switchWorkspace({ workspaceId }),
  );
  tokenStorage.updateWorkspaceSession(response.data);
  return response.data;
}

export async function navigateAfterUserAuth(
  router: Router,
  userAPIService: WorkspaceAPIService,
  tokenStorage: TokenStorageService,
  options: NavigateAfterUserAuthOptions,
): Promise<boolean> {
  if (options.workspace) {
    return navigateAfterLogin(router, {
      workspace: options.workspace,
      returnUrl: options.returnUrl,
    });
  }

  const count = options.workspaceAccessCount ?? 0;
  const workspaceIds = options.workspaceIds ?? [];

  if (count === 0) {
    return navigateToCreateCompany(router, {
      planKey: options.planKey,
      billingCycle: options.billingCycle,
    });
  }

  if (count === 1 && workspaceIds[0]) {
    const authData = await switchToWorkspace(
      userAPIService,
      tokenStorage,
      workspaceIds[0],
    );

    return navigateAfterLogin(router, {
      workspace: authData.workspace,
      returnUrl: options.returnUrl,
    });
  }

  return router.navigate(['/select-workspace'], {
    queryParams: options.returnUrl ? { returnUrl: options.returnUrl } : {},
  });
}
